import { useQuery } from '@tanstack/react-query';
import { flows } from '@repletoryreach/sdk-api';

/**
 * The languages a chatbot can speak (1 November 2026), from `GET /v1/languages`: only those the platform has switched
 * on, in the order to show them. The one list for a chatbot's languages, a contact's language and the flow builder's
 * Update contact step, so a language switched on for chatbots can be set on a contact too.
 *
 * A language is shown by its own name ("isiZulu"), which is never translated, and, when it differs, by its name in the
 * console's language from the browser ("isiZulu (Zulu)", "isiZulu (Zoeloe)"), falling back to the English name the
 * list carries.
 */

export type Language = flows.Language;

export const LANGUAGES_KEY = ['languages'] as const;

const NONE: Language[] = [];

export function useLanguages() {
  const q = useQuery({
    queryKey: LANGUAGES_KEY,
    queryFn: () => flows.listLanguages(),
    staleTime: 10 * 60_000,
    retry: false
  });
  return { languages: q.data?.data ?? NONE, isPending: q.isPending, isError: q.isError, refetch: q.refetch };
}

/** The order the groups read in; a region the console does not know yet still shows, after these. */
export const REGION_ORDER: readonly string[] = ['south_africa', 'africa', 'widely_used'];

/** The list in groups by region, each group in the list's own order. */
export function byRegion(languages: readonly Language[]): Array<{ region: string; languages: Language[] }> {
  const groups = new Map<string, Language[]>();
  for (const l of languages) groups.set(l.region, [...(groups.get(l.region) ?? []), l]);
  const known = REGION_ORDER.filter((r) => groups.has(r));
  const unknown = Array.from(groups.keys()).filter((r) => !REGION_ORDER.includes(r));
  return [...known, ...unknown].map((region) => ({ region, languages: groups.get(region)! }));
}

const displayNames = new Map<string, Intl.DisplayNames | null>();

/** The language's name in the console's language when the browser knows it, else the English name from the list. */
export function localName(l: Pick<Language, 'code' | 'name'>, consoleLocale: string): string {
  if (!displayNames.has(consoleLocale)) {
    try {
      displayNames.set(consoleLocale, new Intl.DisplayNames([consoleLocale], { type: 'language', fallback: 'none' }));
    } catch {
      displayNames.set(consoleLocale, null);
    }
  }
  try {
    const named = displayNames.get(consoleLocale)?.of(l.code);
    return named && named.toLowerCase() !== l.code.toLowerCase() ? named : l.name;
  } catch {
    return l.name;
  }
}

/** "isiZulu (Zulu)": its own name, then its name in the console's language when that differs. */
export function languageLabel(l: Language, consoleLocale: string): string {
  const local = localName(l, consoleLocale);
  return local.toLowerCase() === l.native_name.toLowerCase() ? l.native_name : `${l.native_name} (${local})`;
}

/**
 * Options for a language Select: the list in order, and a stored value the list does not have kept as it is, so
 * opening a contact never changes their language by itself.
 */
export function languageOptions(
  languages: readonly Language[],
  consoleLocale: string,
  stored?: string | null
): Array<{ value: string; label: string }> {
  const options = languages.map((l) => ({ value: l.code, label: languageLabel(l, consoleLocale) }));
  const keep = (stored ?? '').trim();
  if (keep && !languages.some((l) => l.code === keep)) options.push({ value: keep, label: keep });
  return options;
}

/**
 * The languages a chatbot speaks, as codes, its main language first (3 November 2026): the list the flow builder's
 * Change language step offers. Mirrors `LanguageScope.OfferedOf` in flows-llm: the main language (resolved by the
 * server into `behaviour.languages.main`), then each offered one the platform still has switched on. `listed` is
 * `useLanguages()`; while it is empty every offered code counts.
 */
export function spokenLanguages(
  bot: Pick<flows.Bot, 'language' | 'behaviour'>,
  listed: readonly Pick<Language, 'code'>[]
): string[] {
  const main = bot.behaviour?.languages?.main || bot.language?.trim() || 'en';
  const out = [main];
  for (const code of bot.behaviour?.languages?.offered ?? []) {
    if (!out.includes(code) && (listed.length === 0 || listed.some((l) => l.code === code))) out.push(code);
  }
  return out;
}

/** `{{name}}`, `{{contact.name ?? there}}`: the name is the part before any `??`, which may itself be translated. */
const PLACEHOLDER = /\{\{\s*([^}?]+?)\s*(?:\?\?[^}]*)?\}\}/g;

function placeholderNames(text: string): Set<string> {
  return new Set(Array.from(text.matchAll(PLACEHOLDER), (m) => m[1].trim()));
}

/**
 * The `{{...}}` parts a translation has lost or added, as the server checks before saving: the names must match the
 * original exactly. Empty lists when the translation is fine.
 */
export function placeholderProblems(original: string, text: string): { missing: string[]; extra: string[] } {
  const wanted = placeholderNames(original);
  const given = placeholderNames(text);
  return {
    missing: [...wanted].filter((n) => !given.has(n)).map((n) => `{{${n}}}`),
    extra: [...given].filter((n) => !wanted.has(n)).map((n) => `{{${n}}}`)
  };
}
